import React, { useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2, Info, Loader2, ShieldAlert, Edit3 } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import {
  ItemTypeChangePreview,
  ItemTypeChangeStrategy,
  ItemTypeChangeManualEntry,
  ItemTypeChangeRequest,
  ItemTypeChangeResult,
  ItemTypeTransitionClass,
  getItemTypeLabel,
} from '../../../../types/itemTypeChange';
import { itemTypeChangeService } from '../../../../services/itemTypeChangeService';
import { ManualRegistrationTable } from './ManualRegistrationTable';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface Step3Props {
  item: ItemsDto;
  preview: ItemTypeChangePreview;
  strategy: ItemTypeChangeStrategy;
  onStrategyChange: (s: ItemTypeChangeStrategy) => void;
  manualEntries: ItemTypeChangeManualEntry[];
  onManualEntriesChange: (entries: ItemTypeChangeManualEntry[]) => void;
  reason: string;
  onReasonChange: (s: string) => void;
  onBack: () => void;
  onDone: () => void;
}

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

const getStrategyLabel = (t: TFunc): Record<ItemTypeChangeStrategy, string> => ({
  [ItemTypeChangeStrategy.Direct]: t('manager.modules.itemTypeChange.strategy.direct', {}, 'Direkte (ingen datamigrering)'),
  [ItemTypeChangeStrategy.Aggregate]: t('manager.modules.itemTypeChange.strategy.aggregate', {}, 'Aggreger rader per lokasjon'),
  [ItemTypeChangeStrategy.Placeholder]: t('manager.modules.itemTypeChange.strategy.placeholder', {}, 'Generer placeholder-verdier'),
  [ItemTypeChangeStrategy.Manual]: t('manager.modules.itemTypeChange.strategy.manual', {}, 'Manuell registrering rad-for-rad'),
});

const getTransitionLabel = (t: TFunc): Record<ItemTypeTransitionClass, string> => ({
  [ItemTypeTransitionClass.Trivial]: t('manager.modules.itemTypeChange.transition.trivial', {}, 'Triviell — ingen datamigrering'),
  [ItemTypeTransitionClass.Downgrade]: t('manager.modules.itemTypeChange.transition.downgrade', {}, 'Nedgradering — aggregering kreves'),
  [ItemTypeTransitionClass.UpgradeWithStock]: t('manager.modules.itemTypeChange.transition.upgrade', {}, 'Oppgradering — nye data kreves'),
});

export const Step3PreviewConfirm: React.FC<Step3Props> = ({
  item,
  preview,
  strategy,
  onStrategyChange,
  manualEntries,
  onManualEntriesChange,
  reason,
  onReasonChange,
  onBack,
  onDone,
}) => {
  const { t } = useTranslation();
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ItemTypeChangeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const strategyLabel = getStrategyLabel(t);
  const transitionLabel = getTransitionLabel(t);

  const hasBlockers = preview.blockers.length > 0;

  // Valideringsstatus for manuell strategi
  const manualValidation = useMemo(() => {
    if (strategy !== ItemTypeChangeStrategy.Manual) return { valid: true, errors: [] as string[] };
    const errors: string[] = [];
    if (preview.binRows.length !== manualEntries.length) {
      errors.push(t('manager.modules.itemTypeChange.validation.allLocationsNeedEntries', {}, 'Alle lokasjoner må ha entries.'));
    }
    for (const row of preview.binRows) {
      const entry = manualEntries.find((e) => e.itemBinsId === row.itemBinsId);
      if (!entry) {
        errors.push(t('manager.modules.itemTypeChange.validation.missingEntry', { '0': row.binCode }, `Lokasjon ${row.binCode}: mangler registrering.`));
        continue;
      }
      if (preview.newItemType === 6) {
        const count = entry.serialNumbers?.filter((s) => s.trim().length > 0).length ?? 0;
        if (Math.abs(count - row.quantity) > 0.001) {
          errors.push(
            t('manager.modules.itemTypeChange.validation.serialCountMismatch', { '0': row.binCode, '1': count, '2': row.quantity }, `Lokasjon ${row.binCode}: ${count} serienumre angitt, ${row.quantity} kreves.`)
          );
        }
        const nonEmpty = entry.serialNumbers?.filter((s) => s.trim().length > 0) ?? [];
        if (new Set(nonEmpty).size !== nonEmpty.length) {
          errors.push(t('manager.modules.itemTypeChange.validation.duplicateSerials', { '0': row.binCode }, `Lokasjon ${row.binCode}: duplikate serienumre.`));
        }
      } else if (preview.newItemType === 4 || preview.newItemType === 5) {
        const batches = entry.batches?.filter((b) => b.batchNo.trim().length > 0) ?? [];
        const sum = batches.reduce((s, b) => s + (b.quantity || 0), 0);
        if (Math.abs(sum - row.quantity) > 0.001) {
          errors.push(
            t('manager.modules.itemTypeChange.validation.batchSumMismatch', { '0': row.binCode, '1': sum, '2': row.quantity }, `Lokasjon ${row.binCode}: sum av batch-mengder ${sum}, ${row.quantity} kreves.`)
          );
        }
        if (preview.newItemType === 4 && batches.some((b) => !b.expiryDate)) {
          errors.push(t('manager.modules.itemTypeChange.validation.expiryRequired', { '0': row.binCode }, `Lokasjon ${row.binCode}: utløpsdato kreves for alle batcher.`));
        }
        const ids = batches.map((b) => b.batchNo);
        if (new Set(ids).size !== ids.length) {
          errors.push(t('manager.modules.itemTypeChange.validation.duplicateBatches', { '0': row.binCode }, `Lokasjon ${row.binCode}: duplikate batch-nummer.`));
        }
      }
    }
    return { valid: errors.length === 0, errors };
  }, [strategy, manualEntries, preview, t]);

  const canExecute =
    !hasBlockers &&
    confirmed &&
    !executing &&
    (strategy !== ItemTypeChangeStrategy.Manual || manualValidation.valid);

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setError(null);
      const req: ItemTypeChangeRequest = {
        itemNo: preview.itemNo,
        newItemType: preview.newItemType,
        strategy,
        reason: reason.trim() || undefined,
        manualEntries:
          strategy === ItemTypeChangeStrategy.Manual ? manualEntries : undefined,
      };
      const res = await itemTypeChangeService.execute(req);
      setResult(res);
    } catch (e: any) {
      setError(e?.message || t('manager.modules.itemTypeChange.step3.executeFailed', {}, 'Utføring feilet'));
    } finally {
      setExecuting(false);
    }
  };

  if (result) {
    const usedPlaceholder = result.strategyUsed === ItemTypeChangeStrategy.Placeholder;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 p-4 rounded-md">
          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-green-800">{t('manager.modules.itemTypeChange.step3.resultTitle', {}, 'Varetype oppdatert')}</h3>
            <p className="text-sm text-green-700 mt-1">{result.message}</p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-green-700">
              <div>
                {t('manager.modules.itemTypeChange.step3.newType', {}, 'Ny type:')}{' '}
                <span className="font-semibold">{getItemTypeLabel(result.newItemType)}</span>
              </div>
              <div>{t('manager.modules.itemTypeChange.step3.rowsUpdated', { '0': result.rowsUpdated }, `Rader oppdatert: ${result.rowsUpdated}`)}</div>
              <div>{t('manager.modules.itemTypeChange.step3.rowsInserted', { '0': result.rowsInserted }, `Rader lagt til: ${result.rowsInserted}`)}</div>
              <div>{t('manager.modules.itemTypeChange.step3.rowsDeleted', { '0': result.rowsDeleted }, `Rader slettet: ${result.rowsDeleted}`)}</div>
            </div>
          </div>
        </div>

        {usedPlaceholder && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
              <Info className="h-4 w-4" />
              {t('manager.modules.itemTypeChange.step3.autoGenTitle', {}, 'Auto-genererte verdier kan korrigeres')}
            </div>
            <p className="text-xs text-amber-800 mb-2">
              {t('manager.modules.itemTypeChange.step3.autoGenDesc', {}, 'Placeholder-verdiene (MIG-* / LEGACY-*) er bare midlertidige. Bruk editoren for å registrere ekte serie-/batchnummer når de er kjent.')}
            </p>
            <a
              href={`/?module=placeholder-editor&itemNo=${encodeURIComponent(result.itemNo)}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline hover:text-amber-700"
            >
              <Edit3 className="h-3 w-3" />
              {t('manager.modules.itemTypeChange.step3.editSerialBatchNow', {}, 'Rediger serie-/batchnummer nå')}
            </a>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onDone}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            {t('manager.modules.itemTypeChange.step3.done', {}, 'Ferdig')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('manager.modules.itemTypeChange.step3.title', {}, 'Steg 3: Gjennomgang og bekreftelse')}</h2>
      </div>

      {/* Oppsummering */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.common.itemLabel', {}, 'Vare:')}</span>{' '}
            <span className="font-mono">{preview.itemNo}</span> — {preview.description || '-'}
          </div>
          <div>
            <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.transition', {}, 'Overgang:')}</span>{' '}
            {getItemTypeLabel(preview.currentItemType)} →{' '}
            <span className="font-semibold">{getItemTypeLabel(preview.newItemType)}</span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.classification', {}, 'Klassifisering:')}</span>{' '}
            {transitionLabel[preview.transitionClass]}
          </div>
          <div>
            <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.locationsAffected', {}, 'Lokasjoner berørt:')}</span>{' '}
            {preview.affectedLocations} {t('manager.modules.itemTypeChange.step3.rowsUnitsSuffix', { '0': preview.itemBinsCount, '1': preview.totalQuantity }, `(${preview.itemBinsCount} rader, ${preview.totalQuantity} enheter)`)}
          </div>
          {preview.existingSerialsCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.existingSerials', {}, 'Eksisterende serienumre:')}</span>{' '}
              {preview.existingSerialsCount}
            </div>
          )}
          {preview.existingBatchesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.existingBatches', {}, 'Eksisterende batcher:')}</span>{' '}
              {preview.existingBatchesCount}
            </div>
          )}
          {preview.openPListLinesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.openPickLines', {}, 'Åpne plukklinjer:')}</span>{' '}
              {preview.openPListLinesCount}
            </div>
          )}
          {preview.openPOrderLinesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">{t('manager.modules.itemTypeChange.step3.openPurchaseOrders', {}, 'Åpne innkjøpsordrer:')}</span>{' '}
              {preview.openPOrderLinesCount}
            </div>
          )}
        </div>
      </div>

      {/* Blockers */}
      {preview.blockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
            <ShieldAlert className="h-4 w-4" />
            {t('manager.modules.itemTypeChange.step3.blocked', {}, 'Blokkert')}
          </div>
          {preview.blockers.map((b, i) => (
            <p key={i} className="text-sm text-red-700">
              {b}
            </p>
          ))}
        </div>
      )}

      {/* Warnings */}
      {preview.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            {t('manager.modules.itemTypeChange.step3.warnings', {}, 'Advarsler')}
          </div>
          {preview.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-800">
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Strategivalg */}
      {!hasBlockers && preview.availableStrategies.length > 1 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('manager.modules.itemTypeChange.step3.chooseStrategy', {}, 'Velg migreringsstrategi')}</h3>
          <div className="space-y-2">
            {preview.availableStrategies.map((s) => (
              <label
                key={s}
                className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${
                  strategy === s
                    ? 'bg-red-50 border-red-400 ring-2 ring-red-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="strategy"
                  checked={strategy === s}
                  onChange={() => onStrategyChange(s)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-sm text-gray-900">{strategyLabel[s]}</p>
                  {s === ItemTypeChangeStrategy.Placeholder && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      {preview.newItemType === 6
                        ? t('manager.modules.itemTypeChange.step3.placeholderSerialDesc', {}, 'Genererer unikt MIG-* serienummer per enhet. Kan korrigeres via varetelling senere.')
                        : t('manager.modules.itemTypeChange.step3.placeholderBatchDesc', {}, 'Tildeler LEGACY-{yyyyMMdd} som batch for alle lokasjoner.')}
                    </p>
                  )}
                  {s === ItemTypeChangeStrategy.Manual && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      {t('manager.modules.itemTypeChange.step3.manualStrategyDesc', {}, 'Registrer ekte serie-/batch-verdier per lokasjon i tabellen under.')}
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Manuell registrering */}
      {!hasBlockers && strategy === ItemTypeChangeStrategy.Manual && (
        <ManualRegistrationTable
          binRows={preview.binRows}
          newItemType={preview.newItemType}
          entries={manualEntries}
          onEntriesChange={onManualEntriesChange}
        />
      )}

      {!manualValidation.valid && strategy === ItemTypeChangeStrategy.Manual && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <Info className="h-4 w-4" />
            {t('manager.modules.itemTypeChange.step3.validationErrors', {}, 'Valideringsfeil')}
          </div>
          {manualValidation.errors.map((e, i) => (
            <p key={i} className="text-sm text-amber-800">
              • {e}
            </p>
          ))}
        </div>
      )}

      {/* Begrunnelse */}
      {!hasBlockers && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('manager.modules.itemTypeChange.step3.reasonLabel', {}, 'Begrunnelse (loggføres i ApplicationLog)')}
          </label>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={t('manager.modules.itemTypeChange.step3.reasonPlaceholder', {}, "F.eks. 'Kunde ber om batch-sporing på denne varen fra Q2 2026'")}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
          />
        </div>
      )}

      {/* Bekreftelse */}
      {!hasBlockers && (
        <div className="bg-white border-2 border-red-300 rounded-md p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-800">
              {t('manager.modules.itemTypeChange.step3.confirmTextPart1', {}, 'Jeg forstår at denne endringen påvirker')} <span className="font-semibold">{preview.itemBinsCount}</span>{' '}
              {t('manager.modules.itemTypeChange.step3.confirmTextPart2', {}, 'ItemBins-rader for vare')} <span className="font-mono">{preview.itemNo}</span>. {t('manager.modules.itemTypeChange.step3.confirmTextPart3', {}, 'Endringen loggføres og kan ikke angres automatisk.')}
            </span>
          </label>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 rounded-md">
          <ShieldAlert className="h-4 w-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.buttons.back', {}, 'Tilbake')}
        </button>
        <button
          onClick={handleExecute}
          disabled={!canExecute}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {executing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          {t('manager.modules.itemTypeChange.step3.executeButton', {}, 'Utfør endring')}
        </button>
      </div>
    </div>
  );
};
